import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, type BomCostChangeRecord, type BomLockReason } from '@bms2/shared';
import { TextInput } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { RowControls, TileSectionAction, TileSectionHead } from '../../components/composites/Tile';
import { Money } from '../../components/Money';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import { DealBomEditor } from './DealBomEditor';
import { BOM_PATH, type BomPageAnswer } from './DealBomPage';
import { del, post, useApi, DEALS_PATH } from './api';
import { AttachBomModal, BOM_LINK_PATH } from './BomPickers';
import { BomCostChangeLine } from './BomCostChange';
import pageStyles from './RecordPage.module.css';
import styles from './DealBomList.module.css';
import sharedStyles from '../../components/composites/shared.module.css';

/** One of the Deal's bills of materials as the tile lists it (decision 0565). */
export interface BomListRow {
  id: string;
  name: string;
  /** What the list is for, in the builder's own words; drawn under the name (decision 0609). */
  description?: string | null;
  lineCount: number;
  /** Null for a viewer refused Item.purchasePrice (decision 0276). */
  costCents: number | null;
  /** 🔒 The newest costed change after the Quote went out, drawn as a warning line under the row (decision 1466); absent from an older reply. */
  costChange?: BomCostChangeRecord | null;
}

export interface DealBomListProps {
  dealId: string;
  rows: readonly BomListRow[];
  /** False on a done Deal, Lost or Won by its stage, whose lists are kept for reference and gain none (piece 18; decisions 1466, 1512). */
  editable: boolean;
  /** Why the tile offers nothing, which its foot says: the Deal is Lost, or Won with its card complete (decision 1466). */
  lockReason?: BomLockReason | null;
  /** The Deal page's own answer to whether this viewer sees purchase prices (decision 0276); the tile follows it, as the list's page does. */
  seesCost?: boolean;
  apiBaseUrl?: string;
  onSaved: (message: string) => void;
}

/** The route one of the Deal's lists opens at: the list's own home, naming the Deal it was opened from (decisions 1090, 1092). */
export const bomRoute = (dealId: string, bomId: string) => `/boms/${bomId}?deal=${encodeURIComponent(dealId)}`;

/**
 * 🔒 One list's own lines under its name, read-only (decision 0573). Each list
 * asks for itself, so the reads run in parallel and a list draws as its answer
 * arrives; a list whose read fails draws the shell's failed-read panel in its
 * own place and leaves its neighbours standing. Product owner, 2026-09-19:
 * *"in deals make this more like picture in picture where you show the BOM
 * table."*
 *
 * 🔨 It edits nothing (decision 0565): a line is changed on the list's page.
 */
function BomTileLines({ bomId, dealId, seesCost, apiBaseUrl = '' }: { bomId: string; dealId: string; seesCost: boolean; apiBaseUrl?: string }) {
  const page = useApi<BomPageAnswer>(BOM_PATH(bomId, dealId), apiBaseUrl);
  return (
    <div className={styles.lineTable} data-bom-lines={bomId}>
      <AsyncStatePanel state={page.state} emptyAction={t('deal.boms.loadFailed')} onRetry={page.reload}>
        {(answer) =>
          answer.lines.length === 0 && answer.dividers.length === 0 ? (
            <p className={pageStyles.hint}>{t('deal.bom.empty')}</p>
          ) : (
            <DealBomEditor
              bomId={answer.bomId}
              dealId={dealId}
              lines={answer.lines}
              dividers={answer.dividers}
              seesCost={seesCost}
              editable={false}
              readOnly
              apiBaseUrl={apiBaseUrl}
              onSaved={() => undefined}
              onError={() => undefined}
            />
          )
        }
      </AsyncStatePanel>
    </div>
  );
}

/**
 * The Bill of materials tile (decisions 0565, 0573, 0609): the Deal's lists,
 * one row each — the name as a link to its page, what the list is for, and how
 * many lines it holds — with the table folded under the row, Add BOM and Attach Existing in
 * the section's head, and the pencil and the remove on each row (decision 6461). 🔨 The tile no longer edits a list in place, because a Deal carries
 * several and one grid cannot be all of them; the editing lives on the list's
 * own page. 💰 Every list drew expanded until 2026-09-21, so a Deal carrying
 * several long lists gave the card a page of tables nobody asked for; a folded
 * row also costs the read of its lines nothing until it is opened.
 */
export function DealBomList({ dealId, rows, editable, lockReason = null, seesCost = false, apiBaseUrl = '', onSaved }: DealBomListProps) {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Which lists are open; every row starts folded (decision 0609). */
  const [open, setOpen] = useState<Record<string, boolean>>({});
  /** Attach existing's picker (decision 1093); unmounted when closed, so it opens on an empty search. */
  const [attaching, setAttaching] = useState(false);
  /** A refused Detach, in the server's own sentence (decision 1093). */
  const [refusal, setRefusal] = useState<string | null>(null);

  const detach = async (row: BomListRow) => {
    setRefusal(null);
    const answer = await del(BOM_LINK_PATH(dealId, row.id), apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved(t('deal.boms.detached'));
  };

  const add = async () => {
    setError(null);
    const wanted = name.trim();
    if (!wanted) return setError(t('deal.boms.needsName'));
    setSaving(true);
    try {
      const answer = await post<{ id: string }>(`${DEALS_PATH}/${dealId}/boms`, { name: wanted, description: description.trim() || null }, apiBaseUrl);
      if (!answer.ok) return setError(answer.message);
      setAdding(false);
      setName('');
      setDescription('');
      onSaved(t('deal.boms.added'));
      // The new list opens on its own page in the editor, which is where its lines are typed (decision 1054).
      navigate(`${bomRoute(dealId, answer.data.id)}&mode=edit`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.tile} data-bom-list="true">
      {/* 🔒 Add and Attach Existing stand in the section's head, as a tile's Add stands in its header (decision 6461). */}
      <TileSectionHead title={t('deal.card.boms')} name="boms">
        {editable ? (
          <>
            <span data-bom-action="add">
              <TileSectionAction label={t('deal.boms.add')} onAct={() => { setName(''); setError(null); setAdding(true); }} />
            </span>
            {/* 🔒 Attach existing beside Add (decision 1093). */}
            <span data-bom-action="attach">
              <TileSectionAction label={t('deal.boms.attachExisting')} onAct={() => { setRefusal(null); setAttaching(true); }} />
            </span>
          </>
        ) : null}
      </TileSectionHead>
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      {rows.length === 0 ? <p className={pageStyles.hint}>{t('deal.boms.empty')}</p> : null}
      <ul className={`${sharedStyles.listReset} ${styles.rows}`}>
        {rows.map((row) => {
          const expanded = open[row.id] === true;
          return (
            <li key={row.id} className={styles.row} data-bom-row={row.id} data-bom-row-expanded={expanded ? 'true' : 'false'}>
              {/* The list's own row: its name as the link to its page, the count, the total and the fold (decisions 0573, 0609). */}
              <div className={styles.header}>
                <a
                  className={styles.name}
                  href={bomRoute(dealId, row.id)}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(bomRoute(dealId, row.id));
                  }}
                >
                  {row.name}
                </a>
                <span className={styles.lines}>{row.lineCount === 1 ? t('deal.boms.oneLine') : `${row.lineCount} ${t('deal.boms.lines')}`}</span>
                {/* 🔨 The one render path for money on a screen (decision 0481), so a figure below zero reads red. */}
                {row.costCents === null ? null : <span className={styles.cost}><Money cents={row.costCents} /></span>}
                <button
                  type="button"
                  className={styles.fold}
                  aria-expanded={expanded ? 'true' : 'false'}
                  onClick={() => setOpen((held) => ({ ...held, [row.id]: !expanded }))}
                  data-bom-fold={row.id}
                >
                  {expanded ? t('deal.boms.hideLines') : t('deal.boms.showLines')}
                  <Icon meaning={expanded ? 'collapse' : 'expand'} size="inline" />
                </button>
                {/* 🔒 The Tile's own row controls (decision 6461): the pencil opens the list's page in its editor, and the remove is Detach, which closes this Deal's link and keeps the list (decision 1093); a Lost Deal offers neither. */}
                {editable ? (
                  <span className={styles.controls} data-bom-detach={row.id}>
                    <RowControls
                      button={null}
                      onEdit={() => navigate(`${bomRoute(dealId, row.id)}&mode=edit`)}
                      editLabel={t('deal.boms.edit')}
                      onDelete={() => void detach(row)}
                      deleteLabel={t('deal.boms.detach')}
                    />
                  </span>
                ) : null}
              </div>
              {row.description ? <p className={styles.description} data-bom-description={row.id}>{row.description}</p> : null}
              {/* 🔒 The newest costed change after the Quote went out, in the Warning tone (decision 1466). */}
              <BomCostChangeLine bomId={row.id} change={row.costChange} />
              {/* 🔨 The lines are read when the row is opened and not before: a folded row asks the server for nothing. */}
              {expanded ? <BomTileLines bomId={row.id} dealId={dealId} seesCost={seesCost} apiBaseUrl={apiBaseUrl} /> : null}
            </li>
          );
        })}
      </ul>
      {editable ? null : <p className={pageStyles.hint}>{t(lockReason === 'done' ? 'deal.bom.done' : 'deal.bom.lost')}</p>}
      {attaching ? (
        <AttachBomModal
          dealId={dealId}
          held={rows.map((row) => row.id)}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setAttaching(false)}
          onAttached={() => {
            setAttaching(false);
            onSaved(t('deal.boms.attached'));
          }}
          onAddNew={() => {
            setAttaching(false);
            setName('');
            setError(null);
            setAdding(true);
          }}
        />
      ) : null}
      <Modal
        key={adding ? 'bom-add-open' : 'bom-add-closed'}
        open={adding}
        title={t('deal.boms.add')}
        onCancel={() => setAdding(false)}
        dataModal="deal-bom-add"
        actions={[
          { kind: 'primary', label: t('modal.save'), onAct: () => void add() },
          { kind: 'cancel', label: t('modal.cancel'), onAct: () => setAdding(false) },
        ]}
      >
        {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
        <TextInput chars={32} label={t('deal.boms.name')} value={name} onChange={setName} name="bomName" disabled={saving} />
        {/* 🔒 The list says what it is for as it is made (decision 0609); the page it opens on edits the same words. */}
        <TextInput chars={60} label={t('deal.boms.description')} value={description} onChange={setDescription} placeholder={t('deal.boms.descriptionPlaceholder')} name="bomDescription" disabled={saving} />
      </Modal>
    </div>
  );
}
